<template>
  <div class="cx-input" @mouseenter="isTooltipActive=true" @mouseleave="isTooltipActive=false">
    <cx-label v-if="label" :label="label"/>
    <div class="cx-input-content">
      <div
        :class="`cx-position-${errorPosition}`"
        v-cx-tooltip="{
          text: tooltip,
          show: isTooltipActive && tooltip && !loading && !disabled,
          position: 'top-left',
          offsetTop: -5,
          maxWidth: 400,
        }">
        <div class="cx-input-container" :class="{ 'cx-input-container--error': v$.modelValue && !isInputValid }" :disabled="disabled || null">
          <div class="cx-input-wrapper" :style="hideOnLoading">
            <input
              v-model="innerValue" class="cx-input__input"
              :disabled="disabled"
              :placeholder="placeholder" :readonly="readonly"
              :step="step"
              @keydown.up="startApplyStep(StepOperations.add)"
              @keyup.up="stopApplyStep"
              @keydown.down="startApplyStep(StepOperations.sub)"
              @keyup.down="stopApplyStep"
              @blur="onBlur"/>
          </div>
          <div class="cx-input-appendIcon-container" v-if="appendIcon || showStep">
            <div v-if="!disabled && showStep && type === 'number'" class="cx-input-step">
              <cx-button iconSize="12" flat class="cx-input-step-add" icon="keyboard_arrow_up" @mousedown="() => startApplyStep(StepOperations.add)" @mouseup="stopApplyStep" @mouseout="stopApplyStep"/>
              <cx-button iconSize="12" flat class="cx-input-step-sub" icon="keyboard_arrow_down" @mousedown="() => startApplyStep(StepOperations.sub)" @mouseup="stopApplyStep" @mouseout="stopApplyStep"/>
            </div>
            <cx-icon v-if="appendIcon && type !== 'number'" class="cx-input-icon" :icon="appendIcon" :size="appendIconSize"/>
          </div>
        </div>
        <cx-spinner :size="15" :width="1" v-if="loading"></cx-spinner>
        <span v-if="!isInputValid && showErrors && !loading" class="cx-input-validation">
          {{ errorMessage }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref, reactive, watch, Ref, WritableComputedRef } from 'vue';
import { useVuelidate, ValidationArgs } from '@vuelidate/core';
import CxIcon from '@/components/CxIcon.vue';
import CxButton from '@/components/CxButton.vue';
import CxLabel from '@/components/CxLabel.vue';
import CxSpinner from '@/components/CxSpinner.vue';
import { isNil, floor, ceil, isNumber, round } from 'lodash';

enum StepOperations {
  add = 'add',
  sub = 'sub'
}

type SupportedInputTypes = 'text' | 'number';

interface Props {
  modelValue?: string | number,
  validationRules?: ValidationArgs<unknown> | Ref<ValidationArgs<unknown>>
  type?: SupportedInputTypes
  disabled?: boolean
  placeholder?: string
  label?: string
  showStep?: boolean
  step?: number
  precision?: number
  readonly?: boolean
  icon?: string
  iconSize?: number
  loading?: boolean,
  tooltip?: string,
  errorPosition?: string,
  appendIcon?: string | null,
  appendIconSize?: number,
  showErrors?: boolean,
  immediatelyShowErrors?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  showStep: true,
  step: 1,
  precision: 2,
  iconSize: 9,
  errorPosition: 'bottom',
  appendIconSize: 9,
  appendIcon: null,
  showErrors: true,
  immediatelyShowErrors: false
});

// State for validation
const state = reactive({
  modelValue: props.modelValue
});

const initalizeVuelidate = () => {
  return props.validationRules !== undefined ? useVuelidate(props.validationRules, state) : useVuelidate();
};

let v$ = initalizeVuelidate();

const emit = defineEmits<{(e: 'update:modelValue', value: number | string | undefined) }>();

const isInputValid = ref(true);
const errorMessage = ref('');

const innerValue: WritableComputedRef<string | number | null> = computed({
  get(): string {
    let displayVal = props.modelValue ? `${props.modelValue}` : '';
    if (isNumber(props.modelValue) && !Number.isNaN(props.modelValue) && !isNil(props.precision)) displayVal = `${round(props.modelValue, props.precision)}`;
    return displayVal;
  },
  set(newValue: string): void {
    state.modelValue = newValue;
    if (props.immediatelyShowErrors && v$.value.$errors.length) {
      isInputValid.value = false;
      errorMessage.value = v$.value.$errors[0].$message.toString();
    } else if (props.immediatelyShowErrors && v$.value.$errors.length === 0) {
      isInputValid.value = true;
      errorMessage.value = '';
    }
    if (!props.readonly) {
      emit('update:modelValue', newValue);
    }
  },
});

const onBlur = () => {
  if (!props.immediatelyShowErrors && v$.value.$errors.length) {
    isInputValid.value = false;
    errorMessage.value = v$.value.$errors[0].$message.toString();
  } else if (!props.immediatelyShowErrors && v$.value.$errors.length === 0) {
    isInputValid.value = true;
    errorMessage.value = '';
  }
};

let innerStepInterval: ReturnType<typeof setInterval> = 0;
const isTooltipActive = ref(false);
const hideOnLoading = computed(() => {
  return props.loading ? 'visibility: hidden;' : 'visibility: visible;';
});

const getValueRest = (stringValue: string, stepPrecision: number) => {
  if (isNil(stringValue)) {
    return '';
  }
  const splittedDigits = stringValue.toString().split('.');
  const digits = splittedDigits.length > 1 ? splittedDigits[1] : null;

  if (!digits) {
    return '';
  }
  const restOfNumber = digits.slice(stepPrecision);

  return stepPrecision === 0 ? `.${restOfNumber}` : restOfNumber;
};

const applyStep = (operation: StepOperations) => {
  const operationModel: Record<StepOperations, (base: number, step: number) => number> = {
    [StepOperations.add](base, step) {
      return base + step;
    },
    [StepOperations.sub](base, step) {
      return base - step;
    }
  };

  let number = state.modelValue ? state.modelValue as number : 0;
  if (Number.isNaN(number)) {
    number = 0;
  }
  const splittedStep = (`${props.step}`).split('.');
  const stepPrecision = splittedStep.length > 1 ? splittedStep[1].length : 0;
  const base = number > 0 ? floor(number, stepPrecision) : ceil(number, stepPrecision);
  const result = operationModel[operation](base, props.step);
  state.modelValue = result.toFixed(stepPrecision) + getValueRest(state.modelValue as string, stepPrecision);
  emit('update:modelValue', state.modelValue);
  if (v$.value.$errors.length) {
    isInputValid.value = false;
    errorMessage.value = v$.value.$errors[0].$message.toString();
  } else {
    isInputValid.value = true;
    errorMessage.value = '';
  }
};

const startApplyStep = (operation: StepOperations) => {
  if (!innerStepInterval) {
    applyStep(operation);
    innerStepInterval = setInterval(() => applyStep(operation), 150);
  }
};

const stopApplyStep = () => {
  if (innerStepInterval) {
    clearInterval(innerStepInterval);
    innerStepInterval = 0;
  }
};

watch(() => (props.validationRules), () => {
  v$ = initalizeVuelidate();
  v$.value.$validate();
  if (v$.value.$errors.length) {
    isInputValid.value = false;
    errorMessage.value = v$.value.$errors[0].$message.toString();
  } else {
    isInputValid.value = true;
    errorMessage.value = '';
  }
});

watch(() => (props.modelValue), (newValue) => {
  state.modelValue = newValue;
});

onUnmounted(() => {
  stopApplyStep();
});

</script>

<style lang="less">
@import '../common';

.cx-input {
  font-size: @appFontSize;
  font-family: @appFontFamily;
  color: @textColor;

  .cx-label {
    padding: 0 0 3px 12px;
    text-align: left;
  }

  .cx-input-content {
    display: flex;
    flex-direction: column;
    position: relative;
  }

  .cx-label {
    font-size: @appFontSize;
    font-weight: @appFontWeight;
    padding: 0 0 3px 12px;
    color: @textColorLowlight;
    text-align: start;
  }

  .cx-position-right {
    display: flex;
    align-items: center;

    input {
      flex: 1 0 auto;
    }

    .cx-input-validation {
      flex: 0 0 200px;
    }
  }

  .cx-position-bottom {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    text-align: start;
    position: relative;

    .cx-input-validation {
      position: absolute;
      top: 80%;
      padding-top: 8px;
      flex: 0 0 auto;
    }

    input {
      flex: 0 0 auto;
      width: 100%;
    }
  }

  .cx-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .cx-input-container {
    background-color: @inputBackground;
    display: flex;
    border-radius: @inputBorderRadius;
    caret-color: @textColor;
    color: @textColor;
    width: 100%;

    &[disabled] {
      background-color: @inputBackgroundDisabled;
    }

    &:hover:not([disabled]) {
       background-color: @inputBackgroundHover;
      .cx-input-append-icon {
        color: @textColor;
      }
    }

    &.cx-input-container--error {
      background-color: @inputBackgroundError;
    }

    .cx-input-wrapper {
      flex: 1 1 auto;
      padding: 8px 8px 7px 12px;
    }

    input {
      padding-top: 1px;
      font-size: @appFontSize;
      font-weight: @appFontWeight;
      border-radius: @inputBorderRadius;
      background-color: inherit;
      color: inherit;
      outline: none;
      border: none;

      &:disabled {
        caret-color: @textColorDisabled;
        color: @textColorDisabled;
      }

      &:hover {
        &:not([disabled]) {
          caret-color: @textColorHighlight;
          color: @textColorHighlight;
        }
      }

      &:focus {
        caret-color: @textColorHighlight;
        color: @textColorHighlight;
        outline: none;
        border: none;
      }
    }

    .cx-input-appendIcon-container {
      display: flex;
      flex-direction: column;
      align-self: center;
      padding-right: 8px;
      padding-bottom: 1px;
      color: @textColorDisabled;
    }

    .cx-input-append-icon {
      color: @textColorDisabled;
    }

  }

    .cx-input-step {
      display: flex;
      flex-direction: column;
    }

  .cx-input-validation {
    font-size: @appFontSize;
    font-weight: @appFontWeight;
    padding-left: 3px;
    color: @textColorError;
    left: 0;
  }
}
</style>
